import { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  LineChart, Line, ReferenceLine, Legend,
} from "recharts";
import { AlertCircle, Package } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { OrderItem } from "@/services/suprimentos";
import { InsumoStat, computeRankingInsumosFromItems } from "../chartUtils";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { BAR_HOVER_PROPS, lineDots, barRowFromClick } from "@/utils/rechartsInteraction";

/* ─── Helpers ───────────────────────────────────────────────────────────── */
const MONTHS_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
function mesLabel(key: string) {
  const [y, m] = key.split("-");
  return `${MONTHS_PT[parseInt(m, 10) - 1]}/${y.slice(2)}`;
}

interface MonthEvolution {
  mes:        string;
  precoMedio: number;
  quantidade: number;
  valorTotal: number;
}

function buildEvolution(rawItems: OrderItem[]): MonthEvolution[] {
  // Agrupa por mês da data do pedido (usa orderId como fallback de chave)
  const map = new Map<string, { totalNetPrice: number; count: number; qty: number; valor: number }>();
  for (const it of rawItems) {
    const key = (it.orderDate ?? "0000-00").substring(0, 7) || "0000-00";
    if (!map.has(key)) map.set(key, { totalNetPrice: 0, count: 0, qty: 0, valor: 0 });
    const acc = map.get(key)!;
    const price = it.netPrice > 0 ? it.netPrice : it.unitPrice;
    if (price > 0) { acc.totalNetPrice += price; acc.count++; }
    acc.qty   += it.quantity    ?? 0;
    acc.valor += it.totalAmount ?? 0;
  }
  return Array.from(map.entries())
    .filter(([key]) => key !== "0000-00")
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, acc]) => ({
      mes:        mesLabel(key),
      precoMedio: acc.count > 0 ? acc.totalNetPrice / acc.count : 0,
      quantidade: acc.qty,
      valorTotal: acc.valor,
    }));
}

/* ─── Tooltip customizado ────────────────────────────────────────────────── */
function CustomBarTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: InsumoStat; value: number }> }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 max-w-[280px]">
      <p className="text-xs font-semibold text-slate-700 mb-1.5 leading-snug">{d.nome}</p>
      <p className="text-xs text-slate-500">{d.quantidade} pedido{d.quantidade !== 1 ? "s" : ""}</p>
      {d.valorTotal != null && (
        <p className="text-xs font-medium text-brand-700 mt-0.5">{formatCurrency(d.valorTotal)}</p>
      )}
    </div>
  );
}

/* ─── Tabs do modal ──────────────────────────────────────────────────────── */
type Tab = "evolucao" | "pedidos";

/* ─── Componente principal ───────────────────────────────────────────────── */
interface Props {
  orderItems: OrderItem[];
  loading:    boolean;
  error?:     string | null;
}

export function RankingInsumos({ orderItems, loading, error }: Props) {
  const [selected, setSelected] = useState<InsumoStat | null>(null);
  const [tab, setTab] = useState<Tab>("evolucao");

  const data = useMemo(() => computeRankingInsumosFromItems(orderItems), [orderItems]);

  const evolution = useMemo<MonthEvolution[]>(() => {
    if (!selected?.rawItems?.length) return [];
    return buildEvolution(selected.rawItems);
  }, [selected]);

  const avgPrice = useMemo(() => {
    if (!evolution.length) return 0;
    const valid = evolution.filter((e) => e.precoMedio > 0);
    return valid.length ? valid.reduce((s, e) => s + e.precoMedio, 0) / valid.length : 0;
  }, [evolution]);

  /* Modal dos pedidos que contêm o insumo */
  const selectedOrderIds = useMemo(
    () => new Set(selected?.orderIds ?? []),
    [selected]
  );

  const handleBarClick = (item: unknown) => {
    const entry = barRowFromClick<InsumoStat>(item);
    if (entry) { setSelected(entry); setTab("evolucao"); }
  };

  /* ─── Estados de loading / erro / vazio ─────────────────────────────── */
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Package className="w-4 h-4 text-brand-600" />
          <h3 className="text-sm font-semibold text-slate-700">Ranking de Insumos</h3>
        </div>
        <div className="h-56 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader size="md" label="Carregando insumos do período…" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-red-500" />
          <h3 className="text-sm font-semibold text-slate-700">Ranking de Insumos</h3>
        </div>
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-4 h-4 text-brand-600" />
          <h3 className="text-sm font-semibold text-slate-700">Ranking de Insumos</h3>
        </div>
        <div className="h-40 flex items-center justify-center text-sm text-slate-400">
          Nenhum insumo encontrado no período selecionado.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-brand-600" />
            <h3 className="text-sm font-semibold text-slate-700">Ranking de Insumos</h3>
            <span className="text-xs text-slate-400 font-normal ml-1">(por valor total)</span>
          </div>
          <span className="text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full">
            {data.length} insumo{data.length !== 1 ? "s" : ""}
          </span>
        </div>

        <p className="text-xs text-slate-400 -mt-1">
          Clique em uma barra para ver a evolução de preço e os pedidos relacionados.
        </p>

        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 8, right: 40, top: 4, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis
              type="number"
              tickFormatter={(v: number) => formatCurrency(v, true)}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="nomeAbrev"
              width={160}
              tick={{ fontSize: 10, fill: "#64748b" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomBarTooltip />} cursor={{ fill: "#f1f5f9" }} />
            <Bar
              {...BAR_HOVER_PROPS}
              dataKey="valorTotal"
              name="Valor Total"
              fill="#3b82f6"
              radius={[0, 4, 4, 0]}
              maxBarSize={22}
              cursor="pointer"
              onClick={handleBarClick}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ─── Modal ─────────────────────────────────────────────────────────── */}
      {selected && (
        <Modal
          open
          onClose={() => setSelected(null)}
          title={`Insumo: ${selected.nome}`}
          size="lg"
        >
          <div className="space-y-4">
            {/* Resumo */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-slate-500">Pedidos</p>
                <p className="text-lg font-bold text-slate-800">{selected.quantidade}</p>
              </div>
              <div className="bg-brand-50 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-slate-500">Valor Total</p>
                <p className="text-lg font-bold text-brand-700">{formatCurrency(selected.valorTotal ?? 0)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                <p className="text-xs text-slate-500">Qtd. Comprada</p>
                <p className="text-lg font-bold text-slate-800">{formatNumber(selected.qtdComprada ?? 0, 2)}</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-200">
              {(["evolucao", "pedidos"] as Tab[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition-colors",
                    tab === t
                      ? "border-brand-500 text-brand-700 bg-brand-50"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  )}
                >
                  {t === "evolucao" ? "Evolução de Preço" : `Pedidos (${selected.quantidade})`}
                </button>
              ))}
            </div>

            {/* Evolução */}
            {tab === "evolucao" && (
              <div>
                {evolution.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Sem histórico de preço para este insumo.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={evolution} margin={{ left: 16, right: 16, top: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis
                        tickFormatter={(v: number) => formatCurrency(v, true)}
                        tick={{ fontSize: 10, fill: "#94a3b8" }}
                        axisLine={false}
                        tickLine={false}
                        width={70}
                      />
                      <Tooltip
                        formatter={(v: unknown, name: unknown) => [
                          name === "precoMedio" ? formatCurrency(Number(v)) : formatNumber(Number(v), 2),
                          name === "precoMedio" ? "Preço Médio Unit." : "Qtd. Total",
                        ]}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {avgPrice > 0 && (
                        <ReferenceLine
                          y={avgPrice}
                          stroke="#94a3b8"
                          strokeDasharray="4 2"
                          label={{ value: `Média: ${formatCurrency(avgPrice, true)}`, position: "insideTopRight", fontSize: 10, fill: "#94a3b8" }}
                        />
                      )}
                      <Line
                        type="monotone"
                        dataKey="precoMedio"
                        name="Preço Médio Unit."
                        stroke="#3b82f6"
                        strokeWidth={2}
                        {...lineDots("#3b82f6")}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}

            {/* Pedidos */}
            {tab === "pedidos" && (
              <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                {(selected.rawItems?.length ?? 0) === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">Nenhum dado de pedidos disponível.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Pedido</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Data</th>
                        <th className="text-center px-3 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Qtd.</th>
                        <th className="text-right px-3 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Preço Unit.</th>
                        <th className="text-right px-3 py-2.5 text-xs font-semibold text-slate-600 uppercase tracking-wide">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {/* Agrupa por orderId, um registro por pedido */}
                      {Array.from(
                        selected.rawItems!.reduce((acc, it) => {
                          if (!acc.has(it.orderId)) acc.set(it.orderId, []);
                          acc.get(it.orderId)!.push(it);
                          return acc;
                        }, new Map<string, OrderItem[]>())
                      ).map(([orderId, its]) => (
                        <tr
                          key={orderId}
                          className={cn(
                            "hover:bg-slate-50 transition-colors",
                            selectedOrderIds.has(orderId) ? "bg-brand-50/40" : undefined
                          )}
                        >
                          <td className="px-3 py-2 text-slate-800 font-mono text-xs">#{orderId}</td>
                          <td className="px-3 py-2 text-slate-600 whitespace-nowrap text-xs">
                            {its[0].orderDate?.substring(0, 10) ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-center text-slate-600 text-xs">
                            {formatNumber(its.reduce((s, i) => s + (i.quantity ?? 0), 0), 2)}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-700 text-xs">
                            {formatCurrency(its[0].unitPrice ?? 0)}
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-slate-800 text-xs">
                            {formatCurrency(its.reduce((s, i) => s + (i.totalAmount ?? 0), 0))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                      <tr>
                        <td colSpan={4} className="px-3 py-2 text-xs font-semibold text-slate-500">
                          Total — {selectedOrderIds.size} pedido{selectedOrderIds.size !== 1 ? "s" : ""}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-slate-800 text-sm">
                          {formatCurrency(selected.valorTotal ?? 0)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
